import React, { useMemo } from "react";
import NumberFormat from "react-number-format";

import * as Yup from "yup";
import { Box, Button, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { Drawer } from "components/Measure-data/Drawer/Drawer";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useActionHook } from "hooks/shared-hooks/use-action-hook";
import { useDateFormatSelect, useDebtActions } from "store";
import { useDealDefaultCurrencySelect } from "store/selectors/deal-selectors/deal-selectors";
import type { Adjustment, AdjustmentPayload, AmortizationRow, CurrencyType } from "store/types";

import { currenciesArray, currencySign } from "../../constants";

const textFieldStyles = {
  "& .MuiOutlinedInput-input.MuiSelect-select": {
    padding: "8px 12px",
    fontSize: "14px",
    lineHeight: "16px",
    minHeight: 0
  }
};

const inputStyles = {
  "& .MuiOutlinedInput-input.MuiInputBase-input": {
    padding: "8px 12px",
    fontSize: "14px",
    lineHeight: "16px",
    minHeight: 0,
    height: "auto"
  }
};

const boxStyles = {
  display: "flex",
  alignItems: "center",
  gap: 2,
  bgcolor: "#ECF1F8",
  p: 2,
  borderRadius: 4
};

const titleStyles = {
  display: "flex",
  flexDirection: "row",
  gap: 2,
  mb: 2,
  alignItems: "center",
  justifyContent: "space-between",
  borderBottom: "1px solid #e0e0e0"
};

interface AdjustmentDrawerProps {
  open: boolean;
  onClose: (updated?: boolean) => void;
  dealId: number;
  amortizationRow: AmortizationRow | null;
  adjustment?: Adjustment;
}

export interface AdjustmentFormData {
  id?: number;
  effectiveDate: string;
  currency: CurrencyType;
  principalAdjustment: number;
  interestAdjustment: number;
  pikAdjustment: number;
}

const adjustmentValidationSchema = Yup.object({
  effectiveDate: Yup.date().required("Effective date is required"),
  principalAdjustment: Yup.number().required("Principal adjustment is required"),
  interestAdjustment: Yup.number().required("Interest adjustment is required"),
  pikAdjustment: Yup.number().required("PIK adjustment is required")
});

interface AdjustmentFieldProps {
  label: string;
  name: string;
  value: number;
  currency: CurrencyType;
  onChange: (value: number) => void;
}

const AdjustmentField: React.FC<AdjustmentFieldProps> = ({ label, name, value, currency, onChange }) => {
  return (
    <Box sx={boxStyles}>
      <Typography variant="body2" sx={{ minWidth: 120 }}>
        {label}
      </Typography>
      <NumberFormat
        fullWidth
        name={name}
        thousandSeparator
        isNumericString
        variant="outlined"
        prefix={currencySign[currency]}
        decimalScale={2}
        allowNegative={true}
        customInput={TextField}
        value={value}
        onValueChange={({ value }) => {
          onChange(parseFloat(value) || 0);
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            bgcolor: "white"
          },
          ...inputStyles
        }}
      />
    </Box>
  );
};

const Content: React.FC<AdjustmentDrawerProps> = ({ onClose, dealId, amortizationRow, adjustment }) => {
  const dealCurrency = useDealDefaultCurrencySelect();
  const dateFormat = useDateFormatSelect();

  const { createAdjustmentAction, updateAdjustmentAction } = useDebtActions();
  const { action: createAdjustment, loading: createLoading } = useActionHook(createAdjustmentAction);
  const { action: updateAdjustment, loading: updateLoading } = useActionHook(updateAdjustmentAction);

  const submitting = createLoading || updateLoading;

  const initialValues: AdjustmentFormData = useMemo(() => {
    const currency = adjustment ? adjustment.currency : dealCurrency || "USD";

    return {
      id: adjustment?.id ?? undefined,
      effectiveDate: amortizationRow?.date || "",
      currency,
      principalAdjustment: adjustment?.principal_adjustment ?? 0,
      interestAdjustment: adjustment?.interest_adjustment ?? 0,
      pikAdjustment: adjustment?.pik_adjustment ?? 0
    };
  }, [adjustment, amortizationRow, dealCurrency]);

  const formik = useFormik<AdjustmentFormData>({
    initialValues,
    validationSchema: adjustmentValidationSchema,
    onSubmit: async (values) => {
      const payload: AdjustmentPayload = {
        date: values.effectiveDate,
        currency: values.currency,
        principal_adjustment: values.principalAdjustment,
        interest_adjustment: values.interestAdjustment,
        pik_adjustment: values.pikAdjustment
      };

      if (adjustment?.id) {
        await updateAdjustment(dealId, adjustment.id, payload);
      } else {
        await createAdjustment(dealId, payload);
      }
      onClose(true);
    }
  });

  const handleDateChange = (date: dayjs.Dayjs) => {
    const formattedDate = date.format("YYYY-MM-DD");
    formik.setFieldValue("effectiveDate", formattedDate);
  };

  const handleValueChange = (fieldName: string, value: number) => {
    formik.setFieldValue(fieldName, value);
  };

  const totalAdjustment = useMemo(() => {
    return (
      (formik.values.principalAdjustment || 0) +
      (formik.values.interestAdjustment || 0) +
      (formik.values.pikAdjustment || 0)
    );
  }, [formik.values.principalAdjustment, formik.values.interestAdjustment, formik.values.pikAdjustment]);

  return (
    <>
      <form onSubmit={formik.handleSubmit}>
        <Stack sx={{ p: 5, height: "calc(100vh - 50px)" }}>
          <Stack spacing={3} sx={{ overflowY: "auto", overflowX: "hidden", flex: 1 }}>
            {/* General Section */}
            <Stack spacing={2} gap={2} paddingTop={2}>
              <Box sx={{ display: "grid", gridTemplateColumns: "4fr 1fr", gap: 3 }}>
                <DatePicker
                  name="effectiveDate"
                  label="Effective Date"
                  format={dateFormat}
                  views={["year", "month", "day"]}
                  value={formik.values.effectiveDate ? dayjs(formik.values.effectiveDate) : null}
                  onChange={(value) => value && handleDateChange(value)}
                  sx={{ width: "100%", ...inputStyles }}
                  slotProps={{
                    textField: {
                      error: formik.touched.effectiveDate && Boolean(formik.errors.effectiveDate),
                      helperText: formik.touched.effectiveDate && formik.errors.effectiveDate
                    }
                  }}
                />

                <TextField
                  select
                  name="currency"
                  label="Currency"
                  value={formik.values.currency}
                  onChange={formik.handleChange}
                  fullWidth
                  size="small"
                  sx={textFieldStyles}
                >
                  {currenciesArray.map((currency) => (
                    <MenuItem key={currency} value={currency}>
                      {currency}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Stack>

            {/* Adjustable Items Section */}
            <Stack spacing={2}>
              <Stack sx={titleStyles}>
                <Typography variant="h6" fontSize={14}>
                  Adjustable Items
                </Typography>
              </Stack>

              <AdjustmentField
                label="Principal"
                name="principalAdjustment"
                value={formik.values.principalAdjustment}
                currency={formik.values.currency}
                onChange={(value) => handleValueChange("principalAdjustment", value)}
              />

              <AdjustmentField
                label="Interest"
                name="interestAdjustment"
                value={formik.values.interestAdjustment}
                currency={formik.values.currency}
                onChange={(value) => handleValueChange("interestAdjustment", value)}
              />

              <AdjustmentField
                label="PIK"
                name="pikAdjustment"
                value={formik.values.pikAdjustment}
                currency={formik.values.currency}
                onChange={(value) => handleValueChange("pikAdjustment", value)}
              />

              {/* Total Row */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  bgcolor: "#D1E0F0",
                  p: 2,
                  borderRadius: 4,
                  mt: 1
                }}
              >
                <Typography variant="body2" sx={{ minWidth: 120, fontWeight: 600 }}>
                  Total
                </Typography>
                <NumberFormat
                  fullWidth
                  thousandSeparator
                  isNumericString
                  variant="outlined"
                  prefix={currencySign[formik.values.currency as CurrencyType]}
                  decimalScale={2}
                  allowNegative={true}
                  customInput={TextField}
                  value={totalAdjustment}
                  disabled
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "white"
                    },
                    ...inputStyles
                  }}
                />
              </Box>
            </Stack>
          </Stack>
          {/* Action Buttons */}
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 4 }}>
            <Button variant="outlined" onClick={() => onClose()}>
              Cancel
            </Button>
            <Button variant="contained" type="submit" disabled={submitting}>
              {adjustment?.id ? "Update" : "Create"}
            </Button>
          </Box>
        </Stack>
      </form>
    </>
  );
};

export const AdjustmentDrawer: React.FC<AdjustmentDrawerProps> = ({
  open,
  onClose,
  dealId,
  amortizationRow,
  adjustment
}) => {
  return (
    <Drawer anchor="left" open={open} handleClose={() => onClose()} title="Adjustment">
      {dealId && (
        <AsyncContentMui isLoading={false}>
          <Content
            open={open}
            onClose={onClose}
            dealId={dealId}
            amortizationRow={amortizationRow}
            adjustment={adjustment}
          />
        </AsyncContentMui>
      )}
    </Drawer>
  );
};
